// How a cell looks: its value shown in its number format (automatic, number, currency, percent, date, time, plain
// text), and its type (bold, italic, strike), colours from the theme's palette, alignment and wrapping. The same words
// show in the browser and in what an agent reads (read_sheet), so both always agree.

import { linkOf } from "./links";
import { isErr, ymd, type Implied, type Scalar } from "./values";
import { MONTHS } from "../calendar";

export type NumFmt = "auto" | "num" | "cur" | "pct" | "date" | "time" | "datetime" | "text";
export const NUM_FMTS: NumFmt[] = ["auto", "num", "cur", "pct", "date", "time", "datetime", "text"];

/** A cell's look. Colours are the theme palette's places (1 to 8), never a raw colour, so every theme reads. */
export interface CellFmt {
  b?: 1;
  i?: 1;
  s?: 1;
  fg?: number;
  bg?: number;
  al?: "l" | "c" | "r";
  wr?: 1;
  nf?: NumFmt;
  dp?: number;
  sym?: string;
  /** a date's style: left out, the region's numbers (28/10/2026 or 10/28/2026); "mon", its month's name (28 Oct 2026
   *  or Oct 28, 2026); "iso", year first (2026-10-28) */
  ds?: DateStyle;
  /** the cell's own link (Fixes 9): a web or mail address its text opens; a cell's alone, never a column's */
  ln?: string;
  /** the cell's note (Fixes 9): words left on it ("waiting on their reply"), shown on hover; kept in its look so it
   *  moves with the cell when rows are sorted, put in or taken out; a cell's alone, never a column's */
  nt?: string;
  /** the cell's borders (Fixes 9, H2b): the sides it has, some of t, r, b and l (layout.ts); a cell's alone, never a
   *  column's, so they move with the cell */
  bd?: string;
  /** the sides of bd drawn thick (2px) rather than thin */
  bk?: string;
}

/** A note holds up to this many characters. */
export const MAX_NOTE = 5000;

/** A note's words, line breaks kept, or null for none. */
export function cleanNote(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.replace(/\r\n?/g, "\n").replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "").trim().slice(0, MAX_NOTE);
  return s || null;
}

export type DateStyle = "mon" | "iso";
export const DATE_STYLES: DateStyle[] = ["mon", "iso"];

export const PALETTE_SIZE = 8;
export const CURRENCIES = ["$", "£", "€", "¥", "₹"];

/** A look cleaned: only what a cell's look has, in range. A whole column's or row's look takes no link, note or border (links: false). */
export function cleanFmt(raw: unknown, links = true): CellFmt | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const f: CellFmt = {};
  if (r.b) f.b = 1;
  if (r.i) f.i = 1;
  if (r.s) f.s = 1;
  if (typeof r.fg === "number" && r.fg >= 1 && r.fg <= PALETTE_SIZE) f.fg = Math.round(r.fg);
  if (typeof r.bg === "number" && r.bg >= 1 && r.bg <= PALETTE_SIZE) f.bg = Math.round(r.bg);
  if (r.al === "l" || r.al === "c" || r.al === "r") f.al = r.al;
  if (r.wr) f.wr = 1;
  if (typeof r.nf === "string" && (NUM_FMTS as string[]).includes(r.nf) && r.nf !== "auto") f.nf = r.nf as NumFmt;
  if (typeof r.dp === "number" && r.dp >= 0 && r.dp <= 10) f.dp = Math.round(r.dp);
  if (typeof r.sym === "string" && CURRENCIES.includes(r.sym)) f.sym = r.sym;
  if (typeof r.ds === "string" && (DATE_STYLES as string[]).includes(r.ds)) f.ds = r.ds as DateStyle;
  const ln = links ? linkOf(r.ln) : null;
  if (ln) f.ln = ln;
  const nt = links ? cleanNote(r.nt) : null;
  if (nt) f.nt = nt;
  const bd = links && typeof r.bd === "string" ? ["t", "r", "b", "l"].filter((k) => (r.bd as string).includes(k)).join("") : "";
  if (bd) {
    f.bd = bd;
    const bk = typeof r.bk === "string" ? ["t", "r", "b", "l"].filter((k) => bd.includes(k) && (r.bk as string).includes(k)).join("") : "";
    if (bk) f.bk = bk;
  }
  return Object.keys(f).length ? f : null;
}

/** Looks laid over each other, the later winning: a column's, a row's, then the cell's own. */
export function mergeFmt(...fs: (CellFmt | null | undefined)[]): CellFmt {
  const out: CellFmt = {};
  for (const f of fs) if (f) Object.assign(out, f);
  return out;
}

function groupInt(s: string): string {
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A number with a fixed count of decimals, grouped by thousands when asked. */
export function fixed(n: number, dp: number, group: boolean): string {
  const neg = n < 0;
  let s = Math.abs(n).toFixed(Math.max(0, Math.min(10, dp)));
  if (group) {
    const [i, d] = s.split(".");
    s = groupInt(i) + (d !== undefined ? "." + d : "");
  }
  return (neg && Number(s.replace(/,/g, "")) !== 0 ? "-" : "") + s;
}

/** A number as an automatic format shows it: up to ten significant digits, an exponent when very large or small. */
export function general(n: number): string {
  if (!Number.isFinite(n)) return "#NUM!";
  if (n === 0) return "0";
  const a = Math.abs(n);
  if (a >= 1e15 || a < 1e-9) return n.toExponential(5).replace(/\.?0+e/, "e").toUpperCase();
  if (Number.isInteger(n)) return String(n);
  const s = n.toPrecision(10);
  return s.includes("e") ? String(Number(s)) : s.replace(/\.?0+$/, "");
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function hms(serial: number): { h: number; m: number; s: number } {
  let secs = Math.round((serial - Math.floor(serial)) * 86400);
  if (secs >= 86400) secs = 86399;
  return { h: Math.floor(secs / 3600), m: Math.floor((secs % 3600) / 60), s: secs % 60 };
}

/**
 * A date as the region writes it: 03/04/2026 day first (as the UK does), 4/3/2026 month first (as the US does); in a
 * style, with its month's name (3 Apr 2026, Apr 3, 2026) or year first (2026-04-03).
 */
export function dateText(serial: number, dmy: boolean, ds?: DateStyle): string {
  const { y, m, d } = ymd(serial);
  const p2 = (n: number) => String(n).padStart(2, "0");
  if (ds === "iso") return `${y}-${p2(m)}-${p2(d)}`;
  if (ds === "mon") return dmy ? `${d} ${MON[m - 1]} ${y}` : `${MON[m - 1]} ${d}, ${y}`;
  return dmy ? `${p2(d)}/${p2(m)}/${y}` : `${m}/${d}/${y}`;
}

/** A time as the region writes it: 15:59 day first (the 24-hour clock), 3:59 PM month first; year-first dates use 15:59. */
export function timeText(serial: number, dmy: boolean, ds?: DateStyle): string {
  const { h, m } = hms(serial);
  const mm = String(m).padStart(2, "0");
  return dmy || ds === "iso" ? `${String(h).padStart(2, "0")}:${mm}` : `${h % 12 || 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
}

/** The format a cell shows in: its own when one is chosen, else how its value was typed or worked out. */
export function effectiveNf(f: CellFmt, implied: Implied | undefined): { nf: NumFmt; dp?: number; sym?: string } {
  if (f.nf && f.nf !== "auto") return { nf: f.nf, dp: f.dp ?? (f.nf === "pct" && implied?.nf === "pct" ? implied.dp : undefined), sym: f.sym ?? implied?.sym };
  if (implied) return { nf: implied.nf, dp: f.dp ?? implied.dp, sym: f.sym ?? implied.sym };
  return { nf: "auto", dp: f.dp };
}

/** A value as the cell shows it. raw: what was typed, for plain text. */
export function display(v: Scalar, f: CellFmt, implied: Implied | undefined, dmy: boolean, raw?: string): string {
  if (v === null) return "";
  if (isErr(v)) return v.code;
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  const e = effectiveNf(f, implied);
  if (e.nf === "text") return raw !== undefined && !raw.startsWith("=") ? raw : typeof v === "number" ? general(v) : v;
  if (typeof v === "string") return v;
  switch (e.nf) {
    case "num":
      return fixed(v, e.dp ?? 2, true);
    case "cur": {
      const s = fixed(Math.abs(v), e.dp ?? 2, true);
      const neg = v < 0 && Number(s.replace(/,/g, "")) !== 0;
      return `${neg ? "-" : ""}${e.sym ?? "$"}${s}`;
    }
    case "pct":
      return `${fixed(v * 100, e.dp ?? 2, true)}%`;
    case "date":
      return v >= -657434 && v <= 2958465 ? dateText(v, dmy, f.ds) : general(v);
    case "time":
      return timeText(v, dmy);
    case "datetime":
      return v >= -657434 && v <= 2958465 ? `${dateText(v, dmy, f.ds)} ${timeText(v, dmy, f.ds)}` : general(v);
    default:
      return e.dp !== undefined ? fixed(v, e.dp, false) : general(v);
  }
}

/** How many decimals a value shows now, for More decimals and Fewer decimals. */
export function shownDecimals(v: Scalar, f: CellFmt, implied: Implied | undefined): number {
  if (typeof v !== "number") return 0;
  const e = effectiveNf(f, implied);
  if (e.dp !== undefined) return e.dp;
  if (e.nf === "num" || e.nf === "cur" || e.nf === "pct") return 2;
  const s = general(v);
  const dot = s.indexOf(".");
  return dot < 0 || s.includes("E") ? 0 : s.length - dot - 1;
}

/** Whether a value sits on the right of its cell when no alignment is chosen: numbers do, as in every spreadsheet. */
export function rightByDefault(v: Scalar, f: CellFmt): boolean {
  if (f.nf === "text") return false;
  return typeof v === "number" || typeof v === "boolean" || isErr(v);
}

// ---------------------------------------------------------------------------
// TEXT(value, "format"): the format codes people use most

/** A number in a spreadsheet's format code: 0.00, #,##0, 0%, $#,##0.00, yyyy-mm-dd, mmm d, h:mm AM/PM and the like. */
export function formatCode(n: number, code: string): string {
  const parts = splitSections(code);
  let sec = parts[0];
  let v = n;
  if (parts.length > 1 && n < 0) {
    sec = parts[1];
    v = -n;
  } else if (parts.length > 2 && n === 0) sec = parts[2];
  const isDate = /(^|[^"\\])[ymdhs]/i.test(sec.replace(/"[^"]*"/g, "")) && !/[0#]/.test(sec.replace(/"[^"]*"/g, ""));
  return isDate ? dateCode(v, sec) : numberCode(v, sec, parts.length > 1 && n < 0);
}

function splitSections(code: string): string[] {
  const out: string[] = [];
  let cur = "";
  let q = false;
  for (const ch of code) {
    if (ch === '"') q = !q;
    if (ch === ";" && !q) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

function numberCode(n: number, sec: string, signed: boolean): string {
  // literal text in quotes or after \, kept as it is; the number's own pattern is the run of 0 # , . in the middle
  let v = n;
  const pct = (sec.replace(/"[^"]*"/g, "").match(/%/g) ?? []).length;
  for (let i = 0; i < pct; i++) v *= 100;
  const m = /[0#?,.]+(?:[eE][+-]0+)?/.exec(sec.replace(/"[^"]*"/g, (s) => "\u0000".repeat(s.length)));
  if (!m) return sec.replace(/"([^"]*)"/g, "$1").replace(/\\(.)/g, "$1");
  const pat = m[0];
  const dot = pat.indexOf(".");
  const intPat = dot >= 0 ? pat.slice(0, dot) : pat;
  const decPat = dot >= 0 ? pat.slice(dot + 1).replace(/,/g, "") : "";
  const group = intPat.includes(",");
  // a comma at the end of the number's pattern divides by a thousand for each
  const scale = (/,+$/.exec(intPat)?.[0].length ?? 0) && !decPat ? (/,+$/.exec(intPat)![0].length) : 0;
  for (let i = 0; i < scale; i++) v /= 1000;
  const minDec = (decPat.match(/0/g) ?? []).length;
  const maxDec = decPat.length;
  const minInt = (intPat.match(/0/g) ?? []).length;
  let s = Math.abs(v).toFixed(maxDec);
  if (maxDec > minDec) s = s.replace(new RegExp(`0{1,${maxDec - minDec}}$`), "").replace(/\.$/, minDec ? "." : "");
  let [ip, dp = ""] = s.split(".");
  if (ip === "0" && minInt === 0) ip = "";
  ip = ip.padStart(minInt, "0");
  if (group) ip = groupInt(ip);
  const num = (v < 0 && !signed && Number(s) !== 0 ? "-" : "") + ip + (dp || (maxDec && minDec) ? "." + dp : "");
  const before = sec.slice(0, m.index).replace(/"([^"]*)"/g, "$1").replace(/\\(.)/g, "$1");
  const after = sec.slice(m.index + pat.length).replace(/"([^"]*)"/g, "$1").replace(/\\(.)/g, "$1");
  return before + num + after;
}

function dateCode(serial: number, sec: string): string {
  const { y, m, d } = ymd(serial);
  const t = hms(serial);
  const ampm = /am\/pm|a\/p/i.test(sec);
  const out: string[] = [];
  const toks = sec.match(/"[^"]*"|\\.|am\/pm|a\/p|y+|m+|d+|h+|s+|./gi) ?? [];
  // "m" after an hour or before seconds is minutes
  const kinds = toks.map((x) => x.toLowerCase());
  for (let i = 0; i < toks.length; i++) {
    const tk = toks[i];
    const k = kinds[i];
    if (tk.startsWith('"')) out.push(tk.slice(1, -1));
    else if (tk.startsWith("\\")) out.push(tk.slice(1));
    else if (k === "am/pm") out.push(t.h < 12 ? (tk[0] === "a" ? "am" : "AM") : tk[0] === "a" ? "pm" : "PM");
    else if (k === "a/p") out.push(t.h < 12 ? "A" : "P");
    else if (k[0] === "y") out.push(k.length <= 2 ? String(y % 100).padStart(2, "0") : String(y));
    else if (k[0] === "d") out.push(k.length === 1 ? String(d) : k.length === 2 ? String(d).padStart(2, "0") : k.length === 3 ? DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] : DAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]);
    else if (k[0] === "h") {
      const h = ampm ? t.h % 12 || 12 : t.h;
      out.push(k.length >= 2 ? String(h).padStart(2, "0") : String(h));
    } else if (k[0] === "s") out.push(k.length >= 2 ? String(t.s).padStart(2, "0") : String(t.s));
    else if (k[0] === "m") {
      const prevTime = kinds.slice(0, i).reverse().find((x) => /^[a-z]/.test(x) && !/^[^a-z]/.test(x));
      const nextTime = kinds.slice(i + 1).find((x) => /^[a-z]/.test(x));
      const minutes = (prevTime && prevTime[0] === "h") || (nextTime && nextTime[0] === "s");
      if (minutes && k.length <= 2) out.push(k.length === 2 ? String(t.m).padStart(2, "0") : String(t.m));
      else out.push(k.length === 1 ? String(m) : k.length === 2 ? String(m).padStart(2, "0") : k.length === 3 ? MON[m - 1] : k.length === 5 ? MONTHS[m - 1][0] : MONTHS[m - 1]);
    } else out.push(tk);
  }
  return out.join("");
}
